import React, { useState } from 'react';
import { StyleSheet, Text, View, Pressable } from 'react-native';
import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import GenericButton from '../../components/molecules/GenericButton';
import RejectOfferModal from '../../components/modal/RejectOfferModal';
import ProofRequestModal from '../../components/modal/ProofRequestModal';
import { IThemeContext, useThemeContext } from '../../contexts';
import { CredentialProofRequest } from '../../types/credentials';

interface ProofRequestProps {
  proofRequest: CredentialProofRequest;
  primaryAction: { title: string; action: () => void };
  secondaryAction: { title: string; action: () => void };
  showSuccess?: boolean;
  selectedCredentialIndex: number;
  setSelectedCredentialIndex: (index: number) => void;
}

/**
 * @description Proof request component
 * @param {ProofRequestProps} props - Component properties
 * @return {React.FC}
 */
const ProofRequest: React.FC<ProofRequestProps> = ({
  proofRequest,
  primaryAction,
  secondaryAction,
  showSuccess,
  selectedCredentialIndex,
  setSelectedCredentialIndex
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const { theme } = useThemeContext();
  const styles = createProofRequestStyles(theme);

  const handleCloseModal = () => setIsVisible(false);
  const handleOpenModal = () => setIsVisible(true);
  const handleRejectOffer = (event: any) => {
    event.stopPropagation();
    setRejectVisible(true);
  };

  const handleAcceptOffer = () => {
    primaryAction.action();
  };

  return (
    <>
      {/* Proof Request Modal */}
      <ProofRequestModal
        visible={isVisible}
        onClose={handleCloseModal}
        proofRequest={proofRequest}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        showSuccess={showSuccess}
        selectedCredentialIndex={selectedCredentialIndex}
        setSelectedCredentialIndex={setSelectedCredentialIndex}
        onAccept={handleAcceptOffer}
      />

      {/* Proof List Item */}
      <Pressable onPress={handleOpenModal}>
        <View style={styles.container}>
          {/* Close Icon */}
          <Pressable style={styles.closeIcon} onPress={handleRejectOffer}>
            <Icon path={mdiClose} size={0.8} color={theme.textSecondary} />
          </Pressable>

          {/* Content Container */}
          <View style={styles.contentContainer}>
            {/* Text Section */}
            <View style={styles.textContainer}>
              <Text style={styles.requestText} numberOfLines={3} ellipsizeMode="tail">
                <Text style={styles.requestDetailText}>{proofRequest.requester}</Text> is requesting
                information from your{' '}
                <Text style={styles.requestDetailText}>
                  {proofRequest.credentials[0] ? proofRequest.credentials[0]?.name : 'Credential'}.
                </Text>
              </Text>
              <Text style={styles.requestTimeText}>{proofRequest.receivedTimeAgo}</Text>
            </View>

            {/* Action Section */}
            <View style={styles.actionContainer}>
              <GenericButton
                title="View Request"
                onPress={handleOpenModal}
                isPrimary={true}
                textStyle={styles.viewRequestButtonText}
                buttonStyle={styles.viewRequestButton}
              />
            </View>
          </View>
        </View>
      </Pressable>

      {/* Reject Modal */}
      <RejectOfferModal
        modalVisible={rejectVisible}
        setModalVisible={setRejectVisible}
        rejectAction={secondaryAction.action}
      />
    </>
  );
};

const createProofRequestStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    // Main container styles
    container: {
      position: 'relative',
      borderWidth: 2,
      borderColor: '#D1D5DB',
      borderRadius: 12,
      paddingVertical: 20,
      paddingHorizontal: 16,
      backgroundColor: '#FAFAFA',
      minHeight: 120,
      marginBottom: 8,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2
      },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 3
    },

    // Content container (excludes close icon)
    contentContainer: {
      flex: 1,
      gap: 16,
      paddingRight: 32 // Space for close icon
    },

    // Text container styles
    textContainer: {
      flex: 1,
      gap: 8
    },
    requestText: {
      fontSize: 16,
      fontWeight: '400',
      lineHeight: 22,
      color: theme.textPrimary
    },
    requestDetailText: {
      fontWeight: '600',
      color: theme.textPrimary
    },
    requestTimeText: {
      fontSize: 13,
      fontWeight: '400',
      color: theme.textSecondary,
      marginTop: 4
    },

    // Action container styles
    actionContainer: {
      alignItems: 'stretch'
    },
    viewRequestButton: {
      height: 48,
      backgroundColor: theme.primary,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2
      },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3
    },
    viewRequestButtonText: {
      fontSize: 16,
      color: 'white',
      fontWeight: '600'
    },

    // Close icon styles
    closeIcon: {
      position: 'absolute',
      right: 16,
      top: 16,
      padding: 8,
      zIndex: 1,
      borderRadius: 20,
      backgroundColor: 'rgba(0, 0, 0, 0.05)'
    }
  });

export default ProofRequest;
